import { useEffect, useState } from "react"
import { useDocumentTitle } from "@/hooks/useDocumentTitle"
import { ChevronLeft } from "lucide-react"
import { NavBar } from "@/components/navbar"
import { Button } from "@/components/ui/button"
import { useNavigate } from "react-router-dom"
import UserProfileCard from "@/components/user/UserProfileCard"
import MobileFooterButtons from "@/components/portfolio/settings/MobileFooterButtons"
import { useIsMobile } from "@/hooks/use-mobile"
import { LoadingScreen } from "@/components/ui/loading-screen"
import { useFirebase } from "@/context/FirebaseProvider" // Added
import { useUserOperations } from "@/context/schema/useUserOperations" // Added
import { UserDataInput, User } from "@/schemas/user" // Changed: UserData to UserDataInput, added User
import { toast } from "@/hooks/use-toast" // Added

const UserSettings = () => {
  useDocumentTitle("User Settings - Gozango App")
  const navigate = useNavigate()
  const isMobile = useIsMobile()

  const { userData: firebaseUserData, isLoading: isLoadingUser } = useFirebase() // firebaseUserData is of type User | null | undefined
  const { updateUser: updateUserOp } = useUserOperations(firebaseUserData) // Corrected: remove isLoading destructuring, pass firebaseUserData

  const [formData, setFormData] = useState<Partial<UserDataInput> | null>(null) // Changed: UserData to UserDataInput
  const [isEditing, setIsEditing] = useState(false)
  const [isSaving, setIsSaving] = useState(false) // For save operation status

  useEffect(() => {
    // Initialize formData when firebaseUserData is available
    if (firebaseUserData) {
      // Convert User to Partial<UserDataInput> if necessary, or ensure types are compatible
      // For now, direct assignment assuming UserProfileCard handles it or UserDataInput is a subset of User
      setFormData(firebaseUserData as UserDataInput) // Cast for now, might need more specific mapping
    }
  }, [firebaseUserData])

  // Prevent editing mode from being automatically triggered on mount
  useEffect(() => {
    setIsEditing(false)
  }, [])

  const handleBack = () => {
    navigate(-1)
  }

  const handleCancel = () => {
    if (firebaseUserData) {
      setFormData(firebaseUserData)
    }
    setIsEditing(false)
  }

  const handleSave = async () => {
    if (formData && firebaseUserData?.id) {
      setIsSaving(true)
      try {
        // Ensure we only pass fields that are part of UserDataInput schema for update
        const dataToSave: Partial<UserDataInput> = { ...formData }
        // id should not be part of the data payload for update,
        // UserDataInput from schema doesn't include 'id' directly as a modifiable field.
        // delete (dataToSave as any).id; // if id somehow got into formData

        await updateUserOp(firebaseUserData.id, dataToSave)
        setIsEditing(false)
        toast({ title: "Success", description: "User settings updated." })
        // firebaseUserData will refresh via useFirebase() if the underlying data changes
      } catch (error) {
        console.error("Failed to update user data:", error)
        toast({
          title: "Error",
          description: "Failed to update user settings.",
          variant: "destructive",
        })
      } finally {
        setIsSaving(false)
      }
    }
  }

  if (isLoadingUser && !firebaseUserData) {
    return <LoadingScreen />
  }

  if (!firebaseUserData) {
    // Handle case where user data is definitively not available after loading
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
        <NavBar className="bg-white absolute top-0 w-full" />
        <p className="text-lg text-gray-700 mt-16">
          User data not found. Please try logging in again.
        </p>
        <Button onClick={() => navigate("/login")} className="mt-4">
          Go to Login
        </Button>
      </div>
    )
  }

  // Check if form data is different from user data
  const formEdited =
    formData && firebaseUserData
      ? JSON.stringify(firebaseUserData) !== JSON.stringify(formData)
      : false

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <NavBar className="bg-white" />

      <header className="bg-[#F6F6F7] w-full border-b border-gray-200 shadow-sm">
        <div className="max-w-screen-xl mx-auto px-4 py-4 flex items-center justify-between">
          {!isMobile ? (
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBack}
                className="flex items-center text-gray-600 hover:text-gray-900"
              >
                <ChevronLeft size={16} />
                Back
              </Button>
              <h1 className="text-xl font-semibold">User Settings</h1>
            </div>
          ) : (
            <div className="flex justify-between w-full">
              <h1 className="text-xl font-semibold md:mx-[20px] mx-0">
                User Settings
              </h1>

              <Button
                variant="ghost"
                size="sm"
                onClick={handleBack}
                className="flex items-center text-gray-600 hover:text-gray-900 md:mx-[20px] mx-0"
              >
                <ChevronLeft size={16} />
                Back
              </Button>
            </div>
          )}
        </div>
      </header>

      <div className="flex-1 max-w-screen-xl w-full mx-auto px-6 py-8 mb-16 md:mb-0">
        {/* Ensure firebaseUserData is passed as the original userData prop */}
        {formData && firebaseUserData && (
          <UserProfileCard
            userData={firebaseUserData as User} // Pass firebaseUserData (type User)
            formData={formData as UserDataInput} // Pass formData (type UserDataInput)
            setFormData={setFormData as React.Dispatch<React.SetStateAction<Partial<UserDataInput> | null >>}
            isEditing={isEditing}
            setIsEditing={setIsEditing}
            isLoading={isLoadingUser || isSaving} // Corrected: remove isUpdatingUser
            onSave={handleSave}
            onCancel={handleCancel}
          />
        )}
      </div>

      <MobileFooterButtons
        formEdited={formEdited}
        activeTab="general"
        handleCancel={handleCancel}
        handleSave={handleSave}
        isDisabled={isLoadingUser || isSaving} // Corrected: remove isUpdatingUser
      />
    </div>
  )
}

export default UserSettings
